import ReactECharts from 'echarts-for-react';

/**
 * KernelVersionChart_2 — "minimal editorial" variant of KernelVersionChart (blog05).
 * Same real data: GPT-4 behavior drift, March 2023 → June 2023 (Chen, Zaharia &
 * Zou 2023, arXiv:2307.09009v3 — see KernelVersionChart.tsx header for the
 * per-task citations; mirrors public/blog/gpt4-drift-chen-2023.csv).
 * Opaque white card, generous whitespace, thin strokes, muted slate palette with
 * a single teal accent, small typography, no gradients/glows/shadows.
 */

const INK = {
  teal: '#0AB3BF', // single accent — June bars
  slateLight: '#94A3B8', // March bars, axes, secondary labels
  slate: '#64748B', // primary labels, axis lines
  surface: '#FFFFFF',
  hairline: '#E2E8F0', // subtle gridlines
};

const TASKS = [
  { label: 'Prime vs composite', metric: 'accuracy', n: 1000, march: 84.0, june: 51.1 },
  { label: 'Happy-number counting', metric: 'accuracy', n: 500, march: 83.6, june: 35.2 },
  { label: 'Code generation', metric: 'directly executable', n: 50, march: 52.0, june: 10.0 },
  { label: 'Sensitive questions', metric: 'response rate', n: 100, march: 21.0, june: 5.0 },
];

/** ONE shared percent formatter — tooltip, axis ticks, and bar labels all use it. */
const formatPercent = (v: number) => `${v}%`;

export const KernelVersionChart_2 = ({ height = 320 }: { height?: number }) => {
  const option = {
    backgroundColor: INK.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: INK.hairline, width: 1 } },
      backgroundColor: INK.surface,
      borderColor: INK.slateLight,
      textStyle: { color: INK.slate, fontSize: 12 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const t = TASKS[params[0].dataIndex];
        const rows = params.map((p) => `${p.marker} ${p.seriesName}: <b>${formatPercent(p.value)}</b>`).join('<br/>');
        return `${t.label} — GPT-4 ${t.metric} (n=${t.n})<br/>${rows}`;
      },
    },
    legend: {
      top: 8,
      icon: 'rect',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: INK.slate, fontSize: 11 },
      data: ['March 2023', 'June 2023'],
    },
    grid: { left: 52, right: 32, top: 56, bottom: 64 },
    xAxis: {
      type: 'category',
      data: TASKS.map((t) => t.label),
      axisLine: { lineStyle: { color: INK.slateLight, width: 1 } },
      axisLabel: { color: INK.slate, fontSize: 11, interval: 0, width: 130, overflow: 'break' },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: 'GPT-4 (%)',
      nameTextStyle: { color: INK.slateLight, fontSize: 11 },
      axisLabel: { color: INK.slateLight, fontSize: 11, formatter: (v: number) => formatPercent(v) },
      splitLine: { lineStyle: { color: INK.hairline, width: 1 } },
    },
    series: [
      {
        name: 'March 2023',
        type: 'bar',
        barGap: '12%',
        barWidth: 18,
        itemStyle: { color: INK.slateLight },
        data: TASKS.map((t) => t.march),
        label: {
          show: true,
          position: 'top',
          color: INK.slate,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPercent(p.value),
        },
      },
      {
        name: 'June 2023',
        type: 'bar',
        barWidth: 18,
        itemStyle: { color: INK.teal },
        data: TASKS.map((t) => t.june),
        label: {
          show: true,
          position: 'top',
          color: INK.slate,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPercent(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 text-center">
        Figure 1. Same model name, different behavior, three months apart. Without a versioned contract, your
        agent&apos;s behavior drifts silently. (Chen, Zaharia &amp; Zou 2023, arXiv:2307.09009v3)
      </p>
      <p className="mt-1 text-label-xs text-slate-400 text-center">
        v3 methodology (500 primes + 500 composites); v1&apos;s 97.6%→2.4% figures were revised by the authors.
        &ldquo;Sensitive questions&rdquo; is a response rate, not an accuracy.
      </p>
    </div>
  );
};
